<script lang="ts">
  import type {Comment, ExtendedPoem, Poem, Translation} from '../types'
  import api from '../api/api'
  import {fade} from 'svelte/transition'
  import {replaceValues, t} from '../i18n'
  import NewComment from './NewComment.svelte'
  import {user} from '../stores/user'
  import PoemComment from './PoemComment.svelte'
  import PaginatedCollectionLoader from '../shared/PaginatedCollectionLoader.svelte'
  import SmallPoem from './SmallPoem.svelte'
  import {navigate} from 'svelte5-router'
  import {flash} from '../stores/flash'
  import {modal} from '../stores/modal'
  import AddPoemToCollection from './AddPoemToCollection.svelte'
  import ShareButtons from '../../src/poems/ShareButtons.svelte'
  import ConfirmationModal from '../../src/shared/ConfirmationModal.svelte'
  import NotFoundPage from '../../src/NotFoundPage.svelte'
  import SpinnerSection from '../../src/shared/SpinnerSection.svelte'
  import DropdownWrapper from '../../src/shared/DropdownWrapper.svelte'

  export let handle: string

  let poem: ExtendedPoem | undefined
  let comments: Comment[] = []
  let similarPoems: Poem[] = []
  let translations: Translation[] = []
  let loaded = false

  $: writtenDateRange = [poem?.written_from, poem?.written_to].filter(Boolean).join(' - ')

  async function load(handle: string) {
    loaded = false
    poem = await api.get<ExtendedPoem>(`/poems/${handle}`).catch(() => undefined).finally(() => loaded = true)
    if (!poem) return
    similarPoems = await api.get<Poem[]>(`/poems/${poem.id}/similar?per=3`)
    await api.post(`/poems/${poem.id}/view`)
    poem.views_count = (poem.views_count || 0) + 1
    translations = await api.get<Translation[]>(`/poems/${poem.id}/translations?per=10`)
  }

  $: if (handle) load(handle)

  function onNewComment(c: Comment) {
    comments = [c, ...comments]
  }

  function toggleLike() {
    if (!poem) return

    if (poem.has_liked) {
      unlike()
    } else {
      like()
    }
  }

  async function like() {
    if (!poem) return
    await api.post(`/poems/${poem.id}/like`)
    poem.has_liked = true
    poem.likes_count++
  }

  async function unlike() {
    if (!poem) return

    await api.post(`/poems/${poem.id}/unlike`)
    poem.has_liked = false
    poem.likes_count--
  }

  function openAddToCollection(e: Event) {
    e.preventDefault()
    modal.show(AddPoemToCollection, {props: {poem, onSaved: (ids: number[]) => poem!.collection_ids = ids}})
  }

  async function removePoem() {
    if (!poem) return
    modal.show(ConfirmationModal, {props: {title: t.poem.removeConfirmation, onConfirm: onRemoveConfirm}})
  }

  async function onRemoveConfirm() {
    await api.delete(`/poems/${poem!.id}`)
    navigate('/my-poems')
    flash.show(t.flash.poem.removed)
  }

  function openEdit(e: Event) {
    e.preventDefault()
    navigate(`/poems/${handle}/edit`)
  }
</script>

{#if poem}
  <div class="row">
    <div class="poem-block">
      {#if poem.image_url}
        <div class="poem-cover" style="background-image: url({poem.image_url});"></div>
      {/if}

      <div class="poem-card" class:simple={!poem.image_url}>
        <div class="poem__actions">
          {#each poem.authors as author}
            <a class="poem__author left" href={author.link}>
              <span class="author__photo">
                <img src={author.avatar_big_thumgb} alt={author.full_name}/>
              </span>
              <span class="author__name italic">{author.full_name}</span>
            </a>
          {/each}
          <div class="soc-actions right">
            <div class="soc-item views">
              <i class="icon-p icon-p-eye"></i>
              <span>{poem.views_count}</span>
            </div>
            <div class="soc-item likes">
              <i class="icon-p icon-p-like"></i>
              <span>{poem.likes_count}</span>
            </div>
            <a class="soc-item comments" href="#">
              <i class="icon-p icon-p-comment"></i>
              <span>{poem.comments_count}</span>
            </a>
          </div>
        </div>

        <div class="poem__text">
          <h2 class="rel">
            {poem.title}

            {#if poem.translated_poem?.handle}
              <span class="translation-subheader">
                {@html replaceValues(t.poem.translation_to, {
                  title: poem.translated_poem.title,
                  poem_id: poem.translated_poem.handle
                })}
              </span>
            {/if}

            {#if $user?.id === poem.user_id}
              <span class="poem-edit-btn-wrap db">
                <a class="edit-btn save icon-p-edit" href="/poems/{handle}/edit" onclick={openEdit} title="Edit"></a>
                <span class="edit-btn cancel icon-p-trash" onclick={removePoem} role="button" title="Remove"></span>
              </span>
            {/if}
          </h2>

          <p>{@html poem.content}</p>
        </div>

        <div class="poem__date italic">{writtenDateRange}</div>

        {#if $user}
          <div class="poem__like-wrap">
            <div class="poem__like" class:active={poem.has_liked} onclick={toggleLike}>
              <span class="icon-p-heart heart"/>

              {#if !poem.has_liked}
                <span class="poem__like__text">{t.poem.like.appreciate}</span>
              {:else}
                <span class="poem__like__text">{t.poem.like.appreciated}</span>
              {/if}
            </div>

            <a class="add-to-collection" onclick={openAddToCollection}>
              <span class="icon-p-plus-circle"/>{t.poem.add_to_collection.title}
            </a>
          </div>
        {/if}

        <div class="share-box">
          <div class="total-shares">{t.poem.share}</div>

          <div>
            <ShareButtons {poem}/>
          </div>
        </div>

        {#if translations && translations.length}
          <DropdownWrapper class="ui also-available dropdown" let:show>
            <div class="text italic">{t.poem.also_available_in}</div>
            <i class="dropdown icon icon-p-drop-arrow"></i>
            {#if show}
              <div class="menu" transition:fade={{duration: 100}}>
                {#each translations as translation}
                  <a class="item" href={`/poems/${translation.handle}`}>
                    {t.languages[translation.language_code.toLowerCase()]}
                  </a>
                {/each}
              </div>
            {/if}
          </DropdownWrapper>
        {/if}
      </div>

      <div class="comments-block">
        <h2>
          {t.poem.comments.title}
          <span class="comment-count">({poem.comments_count})</span>
        </h2>

        {#if $user}
          <div class="comment__write">
            <div class="comment__your-photo">
              <img src={$user.avatar_url} alt="Avatar"/>
            </div>

            <NewComment {poem} onAdded={onNewComment}/>
          </div>
        {/if}

        {#each comments as comment}
          <div class="comment__item">
            <PoemComment {poem} {comment}/>
          </div>
        {/each}

        {#if poem}
          <PaginatedCollectionLoader bind:items={comments} url={`/poems/${poem.id}/comments`} per={10} let:loadNext>
            <div class="load-more-comments" onclick={() => loadNext()}>
              {t.poem.show.load_more_comments}
              <i class="icon-p-drop-arrow"/>
            </div>
          </PaginatedCollectionLoader>
        {/if}
      </div>

      {#if similarPoems.length}
        <div class="grid-section">
          <div class="heading with-filter inner-heading">
            <h2>{t.poem.similar.title}</h2>
          </div>

          <div class="grid three">
            {#each similarPoems as poem}
              <div class="grid__item">
                <SmallPoem {poem}/>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
{:else if loaded}
  <NotFoundPage/>
{:else}
  <SpinnerSection/>
{/if}

